import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { Download, ChevronLeft, ChevronRight, DollarSign, Users, Eye, EyeOff } from 'lucide-react';
import * as XLSX from 'xlsx';

const Button = ({ children, className, ...props }) => (
  <button className={`px-4 py-2 rounded-md ${className}`} {...props}>
    {children}
  </button>
);

const Card = ({ children, className }) => (
  <div className={`rounded-lg shadow-lg ${className}`}>
    {children}
  </div>
);

// Ajuste horario (lo dejamos igual que tenías)
const adjustToArgentinaTime = (date) => {
  const argOffset = -3 * 60; // Argentina UTC-3
  const localOffset = date.getTimezoneOffset();
  const adjustedDate = new Date(date.getTime() + (localOffset - argOffset) * 60 * 1000);
  return adjustedDate;
};

const HistoricoVentasPorUsuarios = () => {
  const [ventas, setVentas] = useState({
    ventasDiarias: [],
    totalMes: {
      cantidad_entradas: 0,
      total_ventas: 0,
      cantidad_entradas_anuladas: 0,
      total_ventas_anuladas: 0
    }
  });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [detallesAbiertos, setDetallesAbiertos] = useState({});
  const [detallesVentas, setDetallesVentas] = useState({});
  const [loading, setLoading] = useState(true);

  // Extraer valores con defaults seguros
  const { ventasDiarias = [], totalMes = {} } = ventas;
  const {
    cantidad_entradas = 0,
    total_ventas = 0,
    cantidad_entradas_anuladas = 0,
    total_ventas_anuladas = 0
  } = totalMes;

  useEffect(() => {
    const fetchVentas = async () => {
      try {
        setLoading(true);
        const response = await api.get('/registros/ventas_historicas_por_usuario', {
          params: {
            year: selectedYear,
            month: selectedDate.getMonth() + 1
          }
        });

        // El backend devuelve: { ventasDiarias, totalMes }
        const ventasData = Array.isArray(response.data)
          ? { ventasDiarias: response.data, totalMes: null }
          : response.data || {};

        const ventasCrudas = ventasData.ventasDiarias || [];

        const ventasDiariasNormalizadas = ventasCrudas.map((venta) => ({
          ...venta,
          cantidad_entradas: Number(venta.cantidad_entradas || 0),
          total_ventas: Number(venta.total_ventas || 0),
          cantidad_entradas_anuladas: Number(venta.cantidad_entradas_anuladas || 0),
          total_ventas_anuladas: Number(venta.total_ventas_anuladas || 0),
        }));

        // Totales calculados por si el backend no los mandara
        const totalMesCalculado = {
          cantidad_entradas: ventasDiariasNormalizadas.reduce(
            (sum, v) => sum + v.cantidad_entradas,
            0
          ),
          total_ventas: ventasDiariasNormalizadas.reduce(
            (sum, v) => sum + v.total_ventas,
            0
          ),
          cantidad_entradas_anuladas: ventasDiariasNormalizadas.reduce(
            (sum, v) => sum + v.cantidad_entradas_anuladas,
            0
          ),
          total_ventas_anuladas: ventasDiariasNormalizadas.reduce(
            (sum, v) => sum + v.total_ventas_anuladas,
            0
          ),
        };

        // ✅ Usamos los totales DEL BACKEND si vienen, así aseguramos que
        // lo que ves en el resumen es EXACTAMENTE lo que calcula el servidor.
        const totalMesBackend = ventasData.totalMes || null;

        const totalMesNormalizado = totalMesBackend
          ? {
              cantidad_entradas: Number(totalMesBackend.cantidad_entradas || 0),
              total_ventas: Number(totalMesBackend.total_ventas || 0),
              cantidad_entradas_anuladas: Number(
                totalMesBackend.cantidad_entradas_anuladas || 0
              ),
              total_ventas_anuladas: Number(
                totalMesBackend.total_ventas_anuladas || 0
              ),
            }
          : totalMesCalculado;

        setVentas({
          ventasDiarias: ventasDiariasNormalizadas,
          totalMes: totalMesNormalizado,
        });
      } catch (error) {
        console.error('Error al obtener las ventas:', error);
        setVentas({
          ventasDiarias: [],
          totalMes: {
            cantidad_entradas: 0,
            total_ventas: 0,
            cantidad_entradas_anuladas: 0,
            total_ventas_anuladas: 0,
          },
        });
      } finally {
        setLoading(false);
      }
    };

    fetchVentas();
  }, [selectedYear, selectedDate.getMonth()]);

  // Agrupar ventas por fecha
  const ventasPorFecha = ventasDiarias.reduce((acc, venta) => {
    try {
      const fechaAjustada = adjustToArgentinaTime(new Date(venta.fecha));
      const fecha = format(fechaAjustada, 'yyyy-MM-dd');

      if (!acc[fecha]) {
        acc[fecha] = {
          fechaStr: fecha,
          fechaObj: fechaAjustada,
          ventas: [],
          usuarios: new Set(),
        };
      }

      acc[fecha].ventas.push(venta);
      const nombreUsuario =
        venta['usuario.nombre'] ||
        venta['usuario.usuario.nombre'] ||
        'Usuario desconocido';
      acc[fecha].usuarios.add(nombreUsuario);
    } catch (e) {
      console.error('Error formateando fecha:', e);
    }
    return acc;
  }, {});

  const toggleDetalles = async (fecha, usuario_id) => {
    const key = `${fecha}-${usuario_id}`;

    if (detallesAbiertos[key]) {
      setDetallesAbiertos((prev) => ({ ...prev, [key]: false }));
    } else {
      try {
        setLoading(true);
        // ✅ Mandamos la fecha tal cual 'YYYY-MM-DD'
        const response = await api.get('/registros/detalles_venta', {
          params: {
            usuario_id,
            fecha, // el backend espera 'YYYY-MM-DD'
          },
        });

        setDetallesVentas((prev) => ({
          ...prev,
          [key]: response.data.detalle || [],
        }));

        setDetallesAbiertos((prev) => ({ ...prev, [key]: true }));
      } catch (error) {
        console.error('Error al obtener detalles:', error);
        setDetallesVentas((prev) => ({ ...prev, [key]: [] }));
      } finally {
        setLoading(false);
      }
    }
  };

  const changeMonth = (amount) => {
    setSelectedDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() + amount);
      if (newDate.getFullYear() !== selectedYear) {
        setSelectedYear(newDate.getFullYear());
      }
      return newDate;
    });
  };

  const ventasPorFechaYUsuario = ventasDiarias.reduce((acc, venta) => {
    try {
      const fechaAjustada = adjustToArgentinaTime(new Date(venta.fecha));
      const fecha = format(fechaAjustada, 'yyyy-MM-dd');
      const usuarioId = venta.usuario_id;

      if (!acc[fecha]) {
        acc[fecha] = {
          fechaStr: fecha,
          fechaObj: fechaAjustada,
          usuarios: {},
        };
      }

      if (!acc[fecha].usuarios[usuarioId]) {
        acc[fecha].usuarios[usuarioId] = {
          nombre: venta['usuario.nombre'] || 'Usuario desconocido',
          ventas: [],
        };
      }

      acc[fecha].usuarios[usuarioId].ventas.push(venta);
    } catch (e) {
      console.error('Error formateando fecha:', e);
    }
    return acc;
  }, {});

  const handleYearChange = (e) => {
    const year = parseInt(e.target.value);
    setSelectedYear(year);
    setSelectedDate(new Date(year, selectedDate.getMonth(), 1));
  };

  const handleDownloadMonthly = () => {
    const dataToExport = Object.entries(ventasPorFecha).flatMap(
      ([_, ventasDia]) =>
        ventasDia.ventas.map((venta) => ({
          Fecha: format(
            adjustToArgentinaTime(new Date(venta.fecha)),
            'dd/MM/yyyy'
          ),
          Usuario: venta['usuario.nombre'],
          'Cantidad Entradas': venta.cantidad_entradas,
          'Total Ventas': Number(venta.total_ventas || 0),
          'Entradas Anuladas': venta.cantidad_entradas_anuladas || 0,
          'Ventas Anuladas': Number(venta.total_ventas_anuladas || 0),
        }))
    );

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ventas');
    XLSX.writeFile(workbook, `ventas_${format(selectedDate, 'yyyy-MM')}.xlsx`);
  };

  const handleDownloadDaily = (ventasDia) => {
    const dataToExport = ventasDia.map((venta) => ({
      Fecha: format(
        adjustToArgentinaTime(new Date(venta.fecha)),
        'dd/MM/yyyy'
      ),
      Usuario: venta['usuario.nombre'],
      'Cantidad Entradas': venta.cantidad_entradas,
      'Total Ventas': Number(venta.total_ventas || 0),
      'Entradas Anuladas': venta.cantidad_entradas_anuladas || 0,
      'Ventas Anuladas': Number(venta.total_ventas_anuladas || 0),
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ventas');
    XLSX.writeFile(
      workbook,
      `ventas_${format(
        adjustToArgentinaTime(new Date(ventasDia[0].fecha)),
        'yyyy-MM-dd'
      )}.xlsx`
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1B4F72] to-[#3498DB] text-[#FAFAFA] p-4 md:p-8">
      <motion.header
        className="mb-8 text-center"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-4xl md:text-5xl font-bold text-[#F1C40F] mb-2">
          Histórico de Ventas por Usuario
        </h1>
        <div className="flex justify-center items-center space-x-4">
          <select
            value={selectedDate.getMonth()}
            onChange={(e) => {
              const newDate = new Date(selectedDate);
              newDate.setMonth(parseInt(e.target.value));
              setSelectedDate(newDate);
            }}
            className="bg-[#3498DB] bg-opacity-10 text-[#FAFAFA] border-none rounded-md p-2"
          >
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i} value={i}>
                {format(
                  new Date(selectedDate.getFullYear(), i, 1),
                  'MMMM',
                  { locale: es }
                )}
              </option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={handleYearChange}
            className="bg-[#3498DB] bg-opacity-10 text-[#FAFAFA] border-none rounded-md p-2"
          >
            {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(
              (year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              )
            )}
          </select>
          <p className="text-xl md:text-2xl text-[#FAFAFA]">
            {format(selectedDate, 'MMMM yyyy', { locale: es })}
          </p>
        </div>
      </motion.header>

      <Card className="bg-[#3498DB] bg-opacity-10 backdrop-filter backdrop-blur-lg p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <Button onClick={() => changeMonth(-1)} className="button-secondary">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-2xl font-semibold text-center text-[#F1C40F]">
            Resumen del Mes
          </h2>
          <Button onClick={() => changeMonth(1)} className="button-secondary">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="grid gap-4 md:grid-cols-2 mb-6">
          <div className="text-center">
            <p className="text-lg mb-1 text-[#FAFAFA]">Total de entradas:</p>
            <p className="text-3xl font-bold text-[#3498DB] flex items-center justify-center">
              <Users className="mr-2" />
              {cantidad_entradas}
            </p>
            {cantidad_entradas_anuladas > 0 && (
              <p className="text-sm text-red-300 mt-1">
                Anuladas: {cantidad_entradas_anuladas}
              </p>
            )}
          </div>
          <div className="text-center">
            <p className="text-lg mb-1 text-[#FAFAFA]">Total ventas:</p>
            <p className="text-3xl font-bold text-[#F1C40F] flex items-center justify-center">
              <DollarSign className="mr-2" />
              $
              {Number(total_ventas || 0).toLocaleString('es-AR', {
                minimumFractionDigits: 2,
              })}
            </p>
            {total_ventas_anuladas > 0 && (
              <p className="text-sm text-red-300 mt-1">
                Anuladas: $
                {Number(total_ventas_anuladas || 0).toLocaleString('es-AR', {
                  minimumFractionDigits: 2,
                })}
              </p>
            )}
          </div>
        </div>
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button
            onClick={handleDownloadMonthly}
            className="button-yellow-gold w-full text-xl py-3"
          >
            <Download className="mr-2 inline-block" /> Descargar Informe Mensual
          </Button>
        </motion.div>
      </Card>

      <Card className="bg-[#3498DB] bg-opacity-10 backdrop-filter backdrop-blur-lg p-6">
        <h2 className="text-2xl font-semibold mb-4 text-center text-[#F1C40F]">
          Registros Diarios
        </h2>
        <div className="space-y-6">
          {ventasDiarias.length === 0 ? (
            <p className="text-center py-8 text-[#FAFAFA]">
              No hay datos de ventas para este mes
            </p>
          ) : (
            Object.entries(ventasPorFechaYUsuario)
              .sort(
                ([fechaA], [fechaB]) =>
                  new Date(fechaB) - new Date(fechaA)
              )
              .map(([fecha, grupo]) => (
                <motion.div
                  key={fecha}
                  className="border-b border-[#FAFAFA] border-opacity-20 pb-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <span className="text-xl font-semibold text-[#FAFAFA]">
                        {format(
                          grupo.fechaObj,
                          'EEEE d MMMM yyyy',
                          { locale: es }
                        )}
                      </span>
                    </div>
                    <Button
                      onClick={() =>
                        handleDownloadDaily(
                          Object.values(grupo.usuarios).flatMap(
                            (u) => u.ventas
                          )
                        )
                      }
                      className="button-secondary"
                    >
                      <Download className="mr-1 h-4 w-4" /> Descargar
                    </Button>
                  </div>

                  {Object.entries(grupo.usuarios).map(
                    ([usuarioId, usuarioData]) => (
                      <div
                        key={`${fecha}-${usuarioId}`}
                        className="mb-6"
                      >
                        <h3 className="text-lg font-bold text-[#F1C40F] mb-2">
                          Usuario:{' '}
                          {usuarioData.nombre || 'Nombre no disponible'}
                        </h3>
                        <div className="space-y-4">
                          {usuarioData.ventas.map((venta) => {
                            const key = `${fecha}-${venta.usuario_id}`;
                            return (
                              <div
                                key={key}
                                className="bg-[#3498DB] bg-opacity-20 rounded-lg p-4"
                              >
                                <div className="flex justify-between items-center">
                                  <div>
                                    <div className="grid grid-cols-2 gap-4 mt-2">
                                      <div>
                                        <p className="text-sm text-[#FAFAFA]">
                                          Entradas vendidas:
                                        </p>
                                        <p className="text-lg font-bold text-[#3498DB]">
                                          {venta.cantidad_entradas}
                                        </p>
                                        {venta.cantidad_entradas_anuladas >
                                          0 && (
                                          <p className="text-xs text-red-300 mt-1">
                                            Anuladas:{' '}
                                            {
                                              venta.cantidad_entradas_anuladas
                                            }
                                          </p>
                                        )}
                                      </div>
                                      <div>
                                        <p className="text-sm text-[#FAFAFA]">
                                          Total ventas:
                                        </p>
                                        <p className="text-lg font-bold text-[#F1C40F]">
                                          $
                                          {Number(
                                            venta.total_ventas || 0
                                          ).toLocaleString('es-AR', {
                                            minimumFractionDigits: 2,
                                          })}
                                        </p>
                                        {venta.total_ventas_anuladas > 0 && (
                                          <p className="text-xs text-red-300 mt-1">
                                            Anuladas: $
                                            {Number(
                                              venta.total_ventas_anuladas ||
                                                0
                                            ).toLocaleString('es-AR', {
                                              minimumFractionDigits: 2,
                                            })}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <Button
                                    onClick={() =>
                                      toggleDetalles(
                                        grupo.fechaStr,
                                        venta.usuario_id
                                      )
                                    }
                                    className="button-secondary"
                                  >
                                    {detallesAbiertos[
                                      `${grupo.fechaStr}-${venta.usuario_id}`
                                    ] ? (
                                      <EyeOff className="h-4 w-4" />
                                    ) : (
                                      <Eye className="h-4 w-4" />
                                    )}
                                    <span className="ml-2">
                                      {detallesAbiertos[
                                        `${grupo.fechaStr}-${venta.usuario_id}`
                                      ]
                                        ? 'Ocultar'
                                        : 'Ver'}{' '}
                                      detalles
                                    </span>
                                  </Button>
                                </div>

                                {detallesAbiertos[
                                  `${grupo.fechaStr}-${venta.usuario_id}`
                                ] && (
                                  <div className="mt-4 bg-[#1B4F72] bg-opacity-30 rounded-lg p-4">
                                    <h4 className="text-lg font-semibold mb-2 text-[#F1C40F]">
                                      Detalles por medio de pago
                                    </h4>
                                    {loading ? (
                                      <p className="text-[#FAFAFA]">
                                        Cargando detalles...
                                      </p>
                                    ) : detallesVentas[
                                        `${grupo.fechaStr}-${venta.usuario_id}`
                                      ]?.length > 0 ? (
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {detallesVentas[
                                          `${grupo.fechaStr}-${venta.usuario_id}`
                                        ].map((detalle, index) => (
                                          <div
                                            key={`${grupo.fechaStr}-${venta.usuario_id}-${index}`}
                                            className="bg-[#3498DB] bg-opacity-20 p-3 rounded-lg"
                                          >
                                            <div className="flex items-center justify-between">
                                              <p className="font-bold text-[#FAFAFA]">
                                                {detalle.medio_pago?.nombre ||
                                                  'Medio no especificado'}
                                              </p>
                                              <span
                                                className={`text-xs font-semibold px-2 py-1 rounded-full ${
                                                  detalle.estado ===
                                                  'Anulada'
                                                    ? 'bg-red-500/20 text-red-200 border border-red-400/40'
                                                    : 'bg-green-500/20 text-green-200 border border-green-400/40'
                                                }`}
                                              >
                                                {detalle.estado || 'Activa'}
                                              </span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 mt-2">
                                              <div>
                                                <p className="text-xs text-[#FAFAFA]">
                                                  Entradas:
                                                </p>
                                                <p className="text-sm font-bold text-[#3498DB]">
                                                  {detalle.cantidad_entradas ||
                                                    0}
                                                </p>
                                              </div>
                                              <div>
                                                <p className="text-xs text-[#FAFAFA]">
                                                  Total:
                                                </p>
                                                <p className="text-sm font-bold text-[#F1C40F]">
                                                  $
                                                  {Number(
                                                    detalle.total_ventas || 0
                                                  ).toLocaleString('es-AR', {
                                                    minimumFractionDigits: 2,
                                                  })}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <p className="text-[#FAFAFA]">
                                        No hay detalles disponibles para este
                                        día
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )
                  )}
                </motion.div>
              ))
          )}
        </div>
      </Card>

      <style jsx>{`
        .button-primary {
          background-color: #3498db;
          color: #fafafa;
          padding: 0.5rem 1rem;
          border-radius: 0.375rem;
        }
        .button-primary:hover {
          background-color: #1b4f72;
        }
        .button-secondary {
          background-color: #2ecc71;
          color: #fafafa;
          padding: 0.5rem 1rem;
          border-radius: 0.375rem;
        }
        .button-secondary:hover {
          background-color: #27ae60;
        }
        .button-yellow-gold {
          background-color: #f1c40f;
          color: #fafafa;
          padding: 0.5rem 1rem;
          border-radius: 0.375rem;
        }
        .button-yellow-gold:hover {
          background-color: #d4ac0d;
        }
      `}</style>
    </div>
  );
};

export default HistoricoVentasPorUsuarios;
